import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import {CardHeader, Typography, Box, Chip, Avatar, Paper,Tabs, Tab} from "@mui/material";
import Grid from "@mui/material/Grid";
import {Helmet} from "react-helmet";
import {dateToHumanDateStr, display24HourTime} from "../../shared/date.js";
import Button from "@mui/material/Button";
import {Crumbs} from "../Crumbs.jsx";
import {useEvents} from "../DanceEventProvider.jsx";
import {useEffect, useState} from "react";
import InstagramIcon from '@mui/icons-material/Instagram';


const groupEventsByDate = (events) => {
    const grouped = {};
    events.forEach(e => {
        if (!grouped[e.date]) grouped[e.date] = [];
        grouped[e.date].push(e);
    });
    return grouped;
};

const TwoStepLessons = () => {

    const {events,loading} = useEvents();
    const [instructors, setInstructors] = useState([]);
    const [instructorsLoading, setInstructorsLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [showOlderLessons, setShowOlderLessons] = useState(false);
    const title = "Austin 2 Step Dance Lesson Directory";
    const desc = "Find the best 2-Step lessons in town every day right here!";

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Fetch instructors from API
    useEffect(() => {
        const fetchInstructors = async () => {
            try {
                setInstructorsLoading(true);
                const response = await fetch(`${process.env.REACT_APP_BACKEND_HOST}/instructors?sort=title:asc&populate=pic`);
                const data = await response.json();
                setInstructors(data.data || []);
            } catch (error) {
                console.error('Error fetching instructors:', error);
                setInstructors([]);
            } finally {
                setInstructorsLoading(false);
            }
        };

        fetchInstructors();
    }, []);

    // Filter out events without event_instructors
    const filteredEvents = events.filter(e => Array.isArray(e.event_instructors) && e.event_instructors.length > 0);

    // Group events by date
    const groupedEvents = groupEventsByDate(filteredEvents);
    const sortedDates = Object.keys(groupedEvents).sort();

    // Calculate date ranges (next 3 days and next 30 days)
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    const threeDaysFromNow = new Date();
    threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
    const threeDaysCutoffStr = threeDaysFromNow.toISOString().split('T')[0];
    
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    const thirtyDaysCutoffStr = thirtyDaysFromNow.toISOString().split('T')[0];

    // Separate dates by time ranges (only future/current dates)
    const futureDates = sortedDates.filter(date => date >= todayStr);
    const next3Days = futureDates.filter(date => date <= threeDaysCutoffStr);
    const next4To30Days = futureDates.filter(date => date > threeDaysCutoffStr && date <= thirtyDaysCutoffStr);
    const next30Days = futureDates.filter(date => date <= thirtyDaysCutoffStr);
    
    // Determine which dates to show
    const datesToShow = showOlderLessons ? next30Days : next3Days;

    return <Grid container spacing={2}>
        <Grid item xs={12}>
            <Helmet>
                <meta charSet="utf-8"/>
                <meta property="og:description" content={desc}/>
                <meta property="twitter:title" content={title}/>
                <meta property="twitter:description" content={desc}/>
                <title>{title}</title>
                <meta property="description" content={desc} />
                <link rel="canonical" href="https://austin2step.com/twostep-lessons"/>
            </Helmet>
            <Crumbs crumbs={
                [
                    {title: "Learn"},
                    {title: "Dance Lessons"}
                ]} />
            <Paper>
                <Box p={1}>
                <Typography variant="h6" color="text.secondary">
                    {title}
                </Typography>
                </Box>
            </Paper>
        </Grid>
        
        <Grid item xs={12}>
            <Card>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="lesson tabs">
                        <Tab label="Drop-in Lessons" />
                        <Tab label="Instructors" />
                    </Tabs>
                </Box>
                {/* Tab Panel for Drop-in Lessons */}
                {tabValue === 0 && (
                    <CardContent>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            These are the most popular drop-in 2-step lessons in Austin. Drop-in means you can just show up when the class starts, no partner needed. It's the easiest way to get some two-step experience and then dance afterwards to live music. The true Austin experience.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                           Looking for private lessons, personalized instruction or corporate events? Check out our <Button size="small" onClick={() => setTabValue(1)}>Instructors</Button> to meet the talented teachers in Austin.
                        </Typography>
                        
                        {loading === false && datesToShow.length === 0 && (
                            <Typography color="text.secondary">No lessons found.</Typography>
                        )}
                        
                        {loading === false && datesToShow.map(date => (
                            <Box key={date} sx={{ mb: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    {dateToHumanDateStr(new Date(date + "T12:00:00"))}
                                </Typography>
                                {groupedEvents[date].map((e, i) => (
                                    <Card variant="outlined" key={e.id || i} sx={{ mb: 2 }}>
                                        <CardContent>
                                            <Typography variant="h6">
                                                {e.venue.name} 
                                                <Button size="small" href={`/event/${e.venue.venueKey}/${e.date}`} sx={{ ml: 1 }}>
                                                    Details
                                                </Button>
                                            </Typography>
                                            <Typography variant="overline" color="text.secondary">
                                                {e.venue.location}
                                            </Typography>
                                            {e.event_instructors.map((slot, j) => (
                                                <Box key={j} sx={{ mt: 1 }}>
                                                    <Typography variant="body1">
                                                        {display24HourTime(slot.time)} - {slot.title}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        ))}
                        
                        {loading && (
                            <Typography>Loading lessons...</Typography>
                        )}
                        
                        {/* Show More Button */}
                        {loading === false && next4To30Days.length > 0 && !showOlderLessons && (
                            <Box mt={3} textAlign="center">
                                <Button 
                                    variant="outlined" 
                                    onClick={() => setShowOlderLessons(true)}
                                >
                                    Show More Lessons ({next4To30Days.length} more dates in next 30 days)
                                </Button>
                            </Box>
                        )}
                        
                        {/* Show Less Button */}
                        {loading === false && showOlderLessons && next4To30Days.length > 0 && (
                            <Box mt={3} textAlign="center">
                                <Button 
                                    variant="outlined" 
                                    onClick={() => setShowOlderLessons(false)}
                                >
                                    Show Less (Next 3 days only)
                                </Button>
                            </Box>
                        )}
                    </CardContent>
                )}

                {/* Tab Panel for Instructors */}
                {tabValue === 1 && (
                    <CardContent>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Meet the talented instructors teaching 2-step in Austin. Perfect for private lessons and personalized instruction.
                        </Typography>
                        {instructorsLoading ? (
                            <Typography>Loading instructors...</Typography>
                        ) : instructors.length === 0 ? (
                            <Typography color="text.secondary">No instructors found.</Typography>
                        ) : (
                            <Grid container spacing={2}>
                                {instructors.map((instructor) => (
                                    <Grid item xs={12} sm={6} md={4} key={instructor.id}>
                                        <Card variant="outlined" sx={{ height: '100%' }}>
                                            <CardContent>
                                                <Box display="flex" alignItems="center" mb={2}>
                                                    {instructor.pic?.formats?.thumbnail ? (
                                                        <Avatar 
                                                            src={instructor.pic.formats.thumbnail.url}
                                                            alt={instructor.title}
                                                            sx={{ width: 50, height: 50, mr: 2 }}
                                                        />
                                                    ) : (
                                                        <Avatar sx={{ width: 50, height: 50, mr: 2 }}>
                                                            {instructor.title?.charAt(0) || '?'}
                                                        </Avatar>
                                                    )}
                                                    <Box>
                                                        <Typography variant="h6" component="div">
                                                            {instructor.title}
                                                        </Typography>
                                                        {instructor.specialties && (
                                                            <Box mt={1}>
                                                                {instructor.specialties.split(',').map((specialty, index) => (
                                                                    <Chip 
                                                                        key={index}
                                                                        label={specialty.trim()} 
                                                                        size="small" 
                                                                        sx={{ mr: 0.5, mb: 0.5 }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                                
                                                {instructor.bio && (
                                                    <Typography variant="body2" color="text.secondary" paragraph>
                                                        {instructor.bio}
                                                    </Typography>
                                                )}
                                                
                                                {instructor.website && (
                                                    <Box>
                                                        <Button 
                                                            size="small"
                                                            href={instructor.website}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {instructor.website.replace(/^https?:\/\//, '').replace(/\/$/, '').replace(/^www\./, '')}
                                                        </Button>
                                                    </Box>
                                                )}

                                                {instructor.instagram && (
                                                    <Box>
                                                        <Button 
                                                            size="small"
                                                            href={`https://instagram.com/${instructor.instagram}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            startIcon={<InstagramIcon />}
                                                        >
                                                            {instructor.instagram}
                                                        </Button>
                                                    </Box>
                                                )}
                                                
                                                
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </CardContent>
                )}
            </Card>
        </Grid>
    </Grid>
}

export default TwoStepLessons